import { SessionStorage } from '@pp/core/src/storage/session/SessionStorage';
import { IFRAME_TYPE } from '@pp/core/src/store/slices/launcher/types';
import { SESSION_KEYS } from '@pp/core/src/templateScripts/scripts/bundleLoader/types';
import { DEFAULT_LANGUAGE_CODE } from '@pp/core/src/utils/language';

import type { LauncherTableInfo } from '../context/types';
import { DEVELOPMENT_GAME_URL, GAME_PATH } from '../helpers/constants';

export interface ILauncherSession extends Record<string, string> {
  tables: string;
  gamePath: string;
  preserveUrlSession: string;
  baseUrl: string;
}

const isProduction = process.env.NODE_ENV === 'production';

export class LauncherSession {
  static session?: ILauncherSession | null;
  static sessionKey = 'PP_LAUNCHER';

  public static init(): void {
    const rawSession =
      LauncherSession.getURLSession() ?? LauncherSession.getHistorySession() ?? LauncherSession.getStoredSession();
    if (rawSession) {
      const currentPath = window.location.pathname.replace(/\/+$/, '');
      if (!currentPath.includes('lobby') && currentPath !== rawSession.gamePath) {
        return window.history.back();
      }
      const urlSession = LauncherSession.getURLSession();
      const langFromURL = urlSession ? new URL(urlSession.baseUrl).searchParams.get('lang') : null;
      const language = SessionStorage.getJson<{ lang: string }>(SESSION_KEYS.GAME);
      const tables = JSON.parse(rawSession.tables) as LauncherTableInfo[];
      tables.forEach((table) => {
        const url = new URL(table.url);
        url.searchParams.set('lang', langFromURL ?? language?.lang ?? DEFAULT_LANGUAGE_CODE);
        table.url = url.toString();
      });
      rawSession.tables = JSON.stringify(tables);
      LauncherSession.session = rawSession;
    } else {
      LauncherSession.session = null;
    }
  }

  private static parseUrlSession(): ILauncherSession | null {
    const searchQuery = window.location.search;
    const searchParams = new URLSearchParams(searchQuery);
    const gameLoaderKey = searchParams.get('gameLoaderKey');
    const preserveUrlSession = searchParams.get('preserveUrlSession') || '';
    const tableId = searchParams.get('table_id');

    if (!gameLoaderKey || !tableId) return null;
    const url = isProduction
      ? `${window.location.origin}/${GAME_PATH}/${gameLoaderKey}/${searchQuery}`
      : `${DEVELOPMENT_GAME_URL}/${searchQuery}`;

    const table: LauncherTableInfo = {
      tableId,
      url,
      iframeType: IFRAME_TYPE.TABLE,
    };

    const sessionResult: ILauncherSession = {
      tables: JSON.stringify([table]),
      gamePath: window.location.pathname.replace(/\/+$/, ''),
      preserveUrlSession,
      baseUrl: window.location.href,
    };
    return sessionResult;
  }

  private static getURLSession(): ILauncherSession | null {
    const storedSession = LauncherSession.parseUrlSession();
    return storedSession ?? null;
  }

  private static getHistorySession(): ILauncherSession | null {
    const storedSession = window.history.state;
    return storedSession ?? null;
  }

  private static getStoredSession(): ILauncherSession | null {
    const storedSession = SessionStorage.getJson<ILauncherSession>(LauncherSession.sessionKey);
    return storedSession ?? null;
  }

  public static getSession(): ILauncherSession | null {
    if (LauncherSession.session === undefined) {
      LauncherSession.init();
    }
    return LauncherSession.session || null;
  }

  public static updateSession(tables: string): void {
    const session = LauncherSession.session;
    if (session && !session.preserveUrlSession) {
      session.tables = tables;
      LauncherSession.session = session;
      SessionStorage.setJson(LauncherSession.sessionKey, session);
      window.history.replaceState(session, '', window.location.pathname);
    }
  }
}
